import React, { useEffect, useState } from 'react';
import Cookies2 from 'universal-cookie';
import HeaderOne from '../../header/HeaderOne';
import FooterOne from '../../footer/FooterOne';
import Head from '../../components/Head';
import './Cookies.css';

const COOKIE_OPTIONS = [
  {
    id: 0,
    title: 'Only essential cookies',
    body: 'Just what we need to keep you logged in and the site functional. No tracking, no analytics, no advertising.'
  },
  {
    id: 1,
    title: 'Essential + functionality',
    body: 'Adds preference cookies — language, time zone, dashboard layout. Still no third-party tracking or advertising.'
  },
  {
    id: 2,
    title: 'All cookies',
    body: 'Includes analytics and advertising cookies. Helps us understand which pages work and improve the product.'
  }
];

const Cookies = () => {
  const cookies = new Cookies2();
  const [selected, setSelected] = useState(0);

  const deleteAllExcept = (exceptions = []) => {
    const allCookies = document.cookie.split(';').map(c => c.split('=')[0].trim());
    for (const name of allCookies) {
      if (!exceptions.includes(name)) {
        cookies.remove(name);
      }
    }
  };

  useEffect(() => {
    cookies.set('acknowledged_cookies', true, { path: '/', expires: new Date(Date.now() + 365 * 86400 * 1000) });
    if (cookies.get('functionality_cookies')?.toString() === 'true' && cookies.get('targeting_cookies')?.toString() === 'true') {
      setSelected(2);
    } else if (cookies.get('functionality_cookies')?.toString() === 'true') {
      setSelected(1);
      deleteAllExcept(['token', 'subuser', 'acknowledged_cookies', 'functionality_cookies']);
    } else {
      deleteAllExcept(['token', 'subuser', 'acknowledged_cookies']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (id) => {
    setSelected(id);
    if (id === 0) {
      deleteAllExcept(['token', 'subuser', 'acknowledged_cookies']);
    } else if (id === 1) {
      deleteAllExcept(['token', 'subuser', 'acknowledged_cookies']);
      cookies.set('functionality_cookies', true, { path: '/', expires: new Date(Date.now() + 365 * 86400 * 1000) });
    } else {
      cookies.set('functionality_cookies', true, { path: '/', expires: new Date(Date.now() + 365 * 86400 * 1000) });
      cookies.set('targeting_cookies', true, { path: '/', expires: new Date(Date.now() + 365 * 86400 * 1000) });
    }
    window.location.reload();
  };

  return (
    <>
      <Head title="Cookie Settings" />
      <HeaderOne />

      <main className="ck">
        <div className="ck-shell">

          {/* ---- Header ---- */}
          <header className="ck-hero">
            <span className="ck-eyebrow">Privacy</span>
            <h1 className="ck-h1">Cookie settings</h1>
            <p className="ck-lead">
              Manage how TextLink uses cookies on your visits. Changes take effect immediately — the page will reload after you choose.
            </p>
          </header>

          {/* ---- Preferences ---- */}
          <section className="ck-section">
            <h2 className="ck-h2">Your preference</h2>
            <p className="ck-sub">Pick the level you're comfortable with. You can change this any time.</p>

            <div className="ck-options" role="radiogroup" aria-label="Cookie preferences">
              {COOKIE_OPTIONS.map((opt) => (
                <label
                  key={opt.id}
                  htmlFor={`ck-opt-${opt.id}`}
                  className={`ck-option ${selected === opt.id ? 'ck-option--active' : ''}`}
                >
                  <input
                    type="radio"
                    id={`ck-opt-${opt.id}`}
                    name="cookieOption"
                    value={opt.id}
                    checked={selected === opt.id}
                    onChange={() => handleSelect(opt.id)}
                    className="ck-radio"
                  />
                  <span className="ck-radio-dot" aria-hidden="true" />
                  <span className="ck-option-body">
                    <span className="ck-option-title">{opt.title}</span>
                    <span className="ck-option-text">{opt.body}</span>
                  </span>
                </label>
              ))}
            </div>
          </section>

          {/* ---- Policy ---- */}
          <section className="ck-section">
            <h2 className="ck-h2">Cookie policy</h2>
            <p className="ck-body">
              We use cookies to help improve your experience of our website at{' '}
              <a href="https://textlinksms.com">textlinksms.com</a>. This cookie policy is part of TextLink's privacy policy and covers the use of cookies between your device and our site.
            </p>
            <p className="ck-body">
              We also provide basic information on third-party services we may use, which may also use cookies as part of their service. This policy does not cover their cookies.
            </p>
            <p className="ck-body">
              If you don't wish to accept cookies from us, you can instruct your browser to refuse cookies from{' '}
              <a href="https://textlinksms.com">textlinksms.com</a>. In that case, we may be unable to provide you with some of your desired content and services.
            </p>

            <h3 className="ck-h3">What is a cookie?</h3>
            <p className="ck-body">
              A cookie is a small piece of data that a website stores on your device when you visit. It typically contains information about the website itself, a unique identifier that allows the site to recognize your web browser when you return, additional data that serves the cookie's purpose, and the lifespan of the cookie itself.
            </p>
            <p className="ck-body">
              Cookies are used to enable certain features (e.g. logging in), track site usage (e.g. analytics), store your user settings (e.g. time zone, notification preferences), and to personalize your content (e.g. advertising, language).
            </p>
            <p className="ck-body">
              Cookies set by the website you are visiting are usually referred to as <strong>first-party cookies</strong>. They typically only track your activity on that particular site.
            </p>
            <p className="ck-body">
              Cookies set by other sites and companies are called <strong>third-party cookies</strong>. They can be used to track you on other websites that use the same third-party service.
            </p>

            <h3 className="ck-h3">Types of cookies and how we use them</h3>

            <h4 className="ck-h4">Essential cookies</h4>
            <p className="ck-body">
              Essential cookies are crucial to your experience of a website, enabling core features like user logins, account management, and payment processing. We use essential cookies to enable certain functions on our website.
            </p>

            <h4 className="ck-h4">Performance cookies</h4>
            <p className="ck-body">
              Performance cookies track how you use a website during your visit. Typically this information is anonymous and aggregated. They help us understand usage patterns, identify problems, and improve the experience for everyone. They do not collect personal information about you.
            </p>

            <h4 className="ck-h4">Functionality cookies</h4>
            <p className="ck-body">
              Functionality cookies collect information about your device and any settings you may configure on the website (like language and time zone). With this information, websites can provide you with customized, enhanced, or optimized content and services.
            </p>

            <h4 className="ck-h4">Targeting / advertising cookies</h4>
            <p className="ck-body">
              Targeting/advertising cookies help determine what promotional content is most relevant to you. Websites may use them to deliver targeted advertising or limit the number of times you see an advertisement. These cookies may be set by us (first-party) or by third-party services and may be used to track you on other websites that use the same service.
            </p>
          </section>

        </div>
      </main>

      <FooterOne />
    </>
  );
};

export default Cookies;
